// IMPORT REACT
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// IMPORT PACKAGES
import * as Yup from "yup";
import { Controller, FormProvider, useForm, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import parse from "html-react-parser";
import { components } from "react-select";
import ReactQuill, { Quill } from "react-quill";

// IMPORT UI COMPONENTS
import {
  AppBar,
  Box,
  Button,
  Dialog,
  DialogContent,
  Divider,
  FormControl,
  FormLabel,
  Grid,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import CustomBreadcrumbs from "../../../components/BreadcrumbsComponent/CustomBreadcrumbs";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SkeletonWrapper from "../../../components/Skeleton/SkeletonWrapper";
import { CustomTextField } from "../../../components/CustomFields/TextField";
import { PreferencesContext } from "../../../PreferenceContext";

import {
  CreateDiagnosisTemplate,
  GetDiagnosisTemplateDetail,
  UpdateDiagnosisTemplate,
} from "../../../services/DiagnosisTemplateService";

import { EncryptDecryptService } from "../../../services/common/EncryptionService";
import { AlertType } from "../../../services/models/AlertType";
import DraggableList from "../../../components/Draggable/DraggableList";
import { PAGE_SIZE } from "../../../services/common/const";
import { GetQuickNotesList } from "../../../services/QuickNotesService";
import { SimpleAsyncSelect } from "../../../components/CustomFields/CustomSelect";
import { GetDiagnosisList } from "../../../services/DiagnosisService";
import DiagnosisMasterSave from "../../Diagnosis/DiagnosisMaster/DiagnosisMasterSave";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import QuickNotesSave from "../../QuickNotes/QuickNotesSave";
import CloudQueueOutlinedIcon from "@mui/icons-material/CloudQueueOutlined";
import LoadingButton from "@mui/lab/LoadingButton";
import { insertTextAtCursor } from "../../../shared/CommonFunction";
import DiagnosisSeverityToggle from "../../../components/Animated/DiagnosisSeverityToggle";
import { diagnosisOptions } from "../../../shared/Model/OPD/Diagnosis";

//SCHEMA OF DIAGNOSIS TEMPLATE DETAIL
interface IDiagnosisTemplateDetail {
  diagnosisTemplateDetailId: number;
  diagnosisTemplateId: number;
  displayOrder: number;
  diagnosisId: number;
  diagnosisName: string;
  isDeleted: boolean;
  mode: string;
  diagnosisSeverityLevel: {
    label: string;
    value: string;
    color: string;
    bg: string;
    bordercolor: string;
  } | null;
}

//SCHEMA OF DIAGNOSIS TEMPLATE
interface IDiagnosisTemplate {
  diagnosisTemplateId: number;
  consumerId: number;
  orgId: number;
  branchId: number;
  title: string;
  note: string;
  diagnosisTemplatesDetailColl: Array<IDiagnosisTemplateDetail>;
  isDeleted: boolean;
  isNote: boolean;
}

// SCHEMA FOR PARAMETER
interface Parameter {
  consumerId: string;
  orgId: string;
  branchId: string;
  departmentId: string;
  specialityId: string;
  pageIndex: number;
  pageSize: number;
  sortColumn: string;
  sortDirection: string;
  searchValue: string;
}

// DEFAULT PARAMS -- ADD HERE IF YOU NEED NEW PARAMETER
const parameters: Parameter = {
  specialityId: "",
  pageIndex: 1,
  pageSize: PAGE_SIZE,
  sortColumn: "",
  sortDirection: "",
  searchValue: "",
  consumerId: "",
  orgId: "",
  branchId: "",
  departmentId: "",
};

export default function DiagnosisTemplateSave(Props: any) {
  // PROPS
  const { isForPopUp = false, handleClosePopUp, name, templateId } = Props;

  const inintDiagnosisTemplate: IDiagnosisTemplate = {
    diagnosisTemplateId: 0,
    consumerId: 0,
    orgId: 0,
    branchId: 0,
    title: isForPopUp ? (name ?? "") : "",
    note: "",
    diagnosisTemplatesDetailColl: [],
    isDeleted: false,
    isNote: false,
  };

  // HOOKS AND CONTEXT
  const navigate = useNavigate();
  const edService = new EncryptDecryptService();
  const { userContext, updateUserContext } = useContext(PreferencesContext);

  // STATES -- FOR ERROR HANDLING AND LOADING
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [diagnosisData, setDiagnosisData] = useState<any[]>([]);
  const [param, setParam] = useState(parameters);
  const [quickNoteColl, setQuickNoteColl] = useState<any[]>([]);
  const [selected, setSelected] = useState<any>();

  // REFERENCES
  const diagnosisQuillRef = useRef<ReactQuill | null>(null);

  const [asyncSelectKey, setAsyncSelectKey] = React.useState({
    OPDDiagnosisSearch: 1,
    QuickNoteSearch: 1,
    OPDDiagnosisTypeSearch: 1,
  });

  const [customPopUp, setCustomPopUp] = React.useState({
    open: false,
    name: "",
    code: "",
  });

  const theme = useTheme();
  const smd = useMediaQuery(theme.breakpoints.down("sm"));
  const mdd = useMediaQuery(theme.breakpoints.down("md"));

  function useQuery() {
    const { search } = useLocation();
    return useMemo(() => new URLSearchParams(search), [search]);
  }

  // GETTTING PARAMS
  let urlParams = useQuery();
  let curRawHistoryTemplateId = urlParams.get("q");
  let diagnosisTemplateId = isForPopUp ? (templateId ?? 0) : 0;
  if (
    curRawHistoryTemplateId !== null &&
    curRawHistoryTemplateId !== undefined &&
    isForPopUp === false
  ) {
    let decId = edService.decrypt(curRawHistoryTemplateId);
    if (decId !== "" && decId !== null && decId !== undefined) {
      diagnosisTemplateId = +decId;
    }
  } else {
    if (urlParams.size > 0 && isForPopUp === false) {
      navigate("/not-found");
    }
  }

  const handleClickOpen = (value: boolean, label: string, code: string) => {
    setCustomPopUp({
      ...customPopUp,
      open: value,
      name: label,
      code: code,
    });
  };

  const handleClose = () => {
    setCustomPopUp({ ...customPopUp, open: false });
  };

  // FUNCTIONS
  const handleClickOpenCreatable = (
    value: boolean,
    label: string,
    code: string,
  ) => {
    setCustomPopUp({
      ...customPopUp,
      open: value,
      name: label,
      code: code,
    });
  };

  const [diagnosisSeverityLevelDialog, setDiagnosisSeverityLevelDialog] =
    React.useState({
      open: false,
      value: null,
    });

  const handleManagePopup = async (code: string, data: any) => {
    setCustomPopUp({ ...customPopUp, open: false });
    if (code === "CREATE" && customPopUp.code === "DIAGNOSIS") {
      await fetchDiagnosisList("", true);
      setAsyncSelectKey({
        ...asyncSelectKey,
        OPDDiagnosisSearch: asyncSelectKey.OPDDiagnosisSearch + 1,
      });
    } else if (code === "CREATE" && customPopUp.code === "DIAGNOSIS_TYPE") {
      setAsyncSelectKey({
        ...asyncSelectKey,
        OPDDiagnosisTypeSearch: asyncSelectKey.OPDDiagnosisTypeSearch + 1,
      });
    } else if (code === "QUICK_NOTES_CREATE") {
      await fetchQuickNotesList("", true);
      setAsyncSelectKey({
        ...asyncSelectKey,
        QuickNoteSearch: asyncSelectKey.QuickNoteSearch + 1,
      });
    }
  };

  const handleDiagnosisDetailAdd = (value: any) => {
    const detailColl = methods?.getValues().diagnosisTemplatesDetailColl || [];

    const maxRowNumber = detailColl?.reduce(
      (acc, curr) => (acc = curr.displayOrder > acc ? curr.displayOrder : acc),
      0,
    );
    const severityLevel = diagnosisOptions?.find(
      (x) => x.value === value?.diagnosisSeverityLevel,
    );
    //SCHEMA TYPE
    const newRow: IDiagnosisTemplateDetail = {
      displayOrder: detailColl.length === 0 ? 0 : maxRowNumber + 1,
      diagnosisId: value?.diagnosisId || 0,
      diagnosisName: value?.diagnosisName || "",
      diagnosisTemplateDetailId: 0,
      diagnosisTemplateId: diagnosisTemplateId,
      isDeleted: value.isDeleted,
      mode: "I",
      diagnosisSeverityLevel: severityLevel || null,
    };

    methods.setValue("diagnosisTemplatesDetailColl", [...detailColl, newRow], {
      shouldValidate: true,
    });
  };

  const DiagnosisDetailCard = ({ item }: { item: any }) => {
    return (
      <Box
        sx={{
          display: "flex",
          gap: 2,
          padding: "10px",
          alignItems: "center",
        }}
      >
        <DragIndicatorIcon sx={{ cursor: "grab" }} />
        <Grid container>
          <Grid
            item
            xs={12}
            sm={6}
            sx={{
              display: "flex",
              alignItems: "center",
            }}
          >
            <Typography
              className={
                "blue-link " +
                (selected?.displayOrder === item.displayOrder
                  ? "symptom-text"
                  : " ")
              }
              sx={{
                cursor: "pointer",
              }}
              onClick={() => {
                setSelected(item);
              }}
            >
              {item.diagnosisName}
            </Typography>
          </Grid>
          <Grid
            item
            xs={12}
            sm={6}
            sx={{
              display: "flex",
              alignItems: "center",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Tooltip title="Set Diagnosis Severity Level">
                <Typography
                  variant="body2"
                  onClick={() =>
                    setDiagnosisSeverityLevelDialog({
                      open: true,
                      value: item,
                    })
                  }
                  className="text-link link-cursor"
                  // sx={{ minWidth: 55 }}
                  sx={{
                    cursor: "pointer",
                    bgcolor: item?.diagnosisSeverityLevel?.bg,
                    color: item?.diagnosisSeverityLevel?.color,
                    border: `1px solid ${item?.diagnosisSeverityLevel?.bordercolor}`,
                    fontSize: "11px !important",
                    fontWeight: 600,
                    px: 1.5,
                    py: 0.25,
                    borderRadius: "4px",
                    textTransform: "uppercase",
                    letterSpacing: "0.02em",
                    whiteSpace: "nowrap",
                    minWidth: "90px",
                    textAlign: "center",
                  }}
                >
                  {item?.diagnosisSeverityLevel?.label || (
                    <span style={{ fontSize: 24, fontWeight: 500 }}>+</span>
                  )}
                </Typography>
              </Tooltip>
            </Box>
          </Grid>
        </Grid>
        <CloseRoundedIcon
          onClick={(e) => {
            handleDeleteDiagnosisDetail(item);
          }}
          style={{ color: "#d32f2f", cursor: "pointer" }}
        />
      </Box>
    );
  };

  const diagnosisOptionComponent = (props: any) => {
    return (
      <components.Option {...props}>
        {props.data.__isNew__ && <Box>{props.children}</Box>}
        {props.data && !props.data.__isNew__ && (
          <Box
            dangerouslySetInnerHTML={{
              __html: props?.children,
            }}
          />
        )}
      </components.Option>
    );
  };

  // COMPONENTS
  const quicknotesOptionComponent = (props: any) => {
    return (
      <components.Option {...props}>
        {props.data.__isNew__ && <Box>{props.children}</Box>}
        {props.data && !props.data.__isNew__ && (
          <Box
            dangerouslySetInnerHTML={{
              __html: props?.children,
            }}
          />
        )}
      </components.Option>
    );
  };

  //SCHEMA VALIDATION
  const validationSchema = Yup.object().shape({
    diagnosisTemplateId: Yup.number().required(),
    consumerId: Yup.number().required(),
    orgId: Yup.number().required(),
    branchId: Yup.number().required(),
    title: Yup.string().required("Title is required."),
    isNote: Yup.boolean().nullable(),
    note: Yup.string().nullable(),
    diagnosisTemplatesDetailColl: Yup.array()
      .of(
        Yup.object().shape({
          diagnosisTemplateDetailId: Yup.number().required(),
          diagnosisTemplateId: Yup.number().required(),
          displayOrder: Yup.number().required(),
          diagnosisId: Yup.number().required(),
          diagnosisName: Yup.string().required(),
          isDeleted: Yup.boolean().default(false),
          mode: Yup.string().nullable(),
          diagnosisSeverityLevel: Yup.object()
            .shape({
              value: Yup.string(),
              label: Yup.string(),
            })
            .nullable(),
        }),
      )
      .nullable(),
    isDeleted: Yup.boolean(),
  });

  // FORM HOOK
  const methods = useForm({
    resolver: yupResolver(validationSchema), // IGNORE THIS ERROR
    defaultValues: inintDiagnosisTemplate, // DEFAULT VALUE -- DON'T CREATE STATE FOR IT -- STATE CAN INCREASE SITE LOAD
  });

  const watchedFields = useWatch({
    control: methods.control,
  });

  const diagnosisTemplatesDetailColl =
    watchedFields.diagnosisTemplatesDetailColl;
  console.log(
    "From watch diagnosisTemplatesDetailColl:",
    diagnosisTemplatesDetailColl,
  );

  // const filtereddOPDDiagnosisDetailColl =
  //   diagnosisTemplatesDetailColl?.filter(
  //     (x) => x?.isDeleted === false && x.mode !== "D"
  //   ) || [];

  const filtereddOPDDiagnosisDetailColl =
    diagnosisTemplatesDetailColl?.filter(
      (x) => (x?.isDeleted ?? false) === false && (x?.mode ?? "U") !== "D",
    ) || [];

  const handleopdDiagnosisChange = (value: any) => {
    const { dragIndex, hoverIndex } = value;
    const diagnosisTemplatesDetailColl =
      methods.getValues().diagnosisTemplatesDetailColl || [];

    // Clone the array to avoid direct mutation
    const updatedItems = [...diagnosisTemplatesDetailColl];

    // Get the item at dragIndex
    const draggedItem = updatedItems[dragIndex];

    // Remove the item from its original position (dragIndex)
    updatedItems.splice(dragIndex, 1);

    // Insert the item at hoverIndex
    updatedItems.splice(hoverIndex, 0, draggedItem);

    // Update the displayOrder of each item in the updated array
    const updatedItemsWithDisplayOrder = updatedItems.map((item, index) => {
      return {
        ...item,
        displayOrder: index, // Adjust this if needed to fit your use case
      };
    });

    methods.setValue(
      "diagnosisTemplatesDetailColl",
      updatedItemsWithDisplayOrder,
      {
        shouldValidate: true,
      },
    );
  };
  // ---- DiagnosisSeverityLevel Dialog content ----
  const renderDiagnosisSeverityLevelDialogContent = () => {
    const item = diagnosisSeverityLevelDialog.value;
    if (!item) return null;
    const currentValue = methods.getValues(
      `diagnosisTemplatesDetailColl.${item.displayOrder ?? 0}.diagnosisSeverityLevel`,
    );

    return (
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 0.5,
          margin: "10px",
        }}
      >
        <FormControl fullWidth>
          <FormLabel
            className={`fileds-label-text  ${
              methods.formState.errors.diagnosisTemplatesDetailColl?.[
                item.displayOrder
              ]?.diagnosisSeverityLevel
                ? "validation-message"
                : ""
            }`}
          >
            Diagnosis Severity Level *
          </FormLabel>
          <Controller
            name={`diagnosisTemplatesDetailColl.${item.displayOrder}.diagnosisSeverityLevel`}
            control={methods.control}
            render={({ field }) => (
              <DiagnosisSeverityToggle
                value={field.value?.value || ""}
                options={diagnosisOptions}
                onChange={async (val) => {
                  field.onChange(diagnosisOptions.find((x) => x.value === val));
                  setDiagnosisSeverityLevelDialog({
                    open: false,
                    value: null,
                  });
                }}
              />
            )}
          />

          <Typography className="validation-message">
            {
              methods.formState.errors.diagnosisTemplatesDetailColl?.[
                item.displayOrder
              ]?.diagnosisSeverityLevel?.message
            }
          </Typography>
        </FormControl>
      </Box>
    );
  };

  const handleDeleteDiagnosisDetail = (item: any) => {
    const detailColl = methods.getValues().diagnosisTemplatesDetailColl || [];

    const updatedColl = detailColl?.map((diagnosis) => {
      if (diagnosis.displayOrder === item.displayOrder) {
        return { ...diagnosis, mode: "D", isDeleted: true };
      }
      return diagnosis;
    });

    methods.setValue("diagnosisTemplatesDetailColl", updatedColl, {
      shouldValidate: true,
    });
  };

  // GET DIAGNOSIS TEMPLATE DETAIL
  const getDiagnosisTemplateDetailById = async () => {
    let result: any;
    let params = new URLSearchParams();
    params.append("diagnosisTemplateId", diagnosisTemplateId?.toString());
    params.append("branchId", userContext?.branchId?.toString());
    params.append("organizationId", userContext?.organizationId?.toString());
    params.append("consumerId", userContext?.consumerId?.toString());
    try {
      const res: any = await GetDiagnosisTemplateDetail(params);
      result = res.data.data;
      const detailColl =
        result?.diagnosisTemplatesDetailColl?.map((x: any) => ({
          ...x,
          diagnosisSeverityLevel: x?.diagnosisSeverityLevel
            ? (diagnosisOptions.find(
                (o) => o.value === x.diagnosisSeverityLevel,
              ) ?? null)
            : null,
        })) || [];

      methods.reset({
        ...inintDiagnosisTemplate,
        diagnosisTemplateId: result?.diagnosisTemplateId || 0,
        title: result?.title || "",
        note: result.note ?? "",
        isNote: result.isNote ?? "",
        diagnosisTemplatesDetailColl: detailColl || [],
      });
      return result;
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  };

  // SAVE AND UPDATE EXAMINATION TEMPLATE
  const saveDiagnosisTemplate = async (value: IDiagnosisTemplate) => {
    try {
      let result: any = "";

      const deepCopy = (obj: any) => JSON.parse(JSON.stringify(obj));
      const detailColl = deepCopy(value?.diagnosisTemplatesDetailColl || []);
      // const saveDetailColl = detailColl || [];
      let saveDetailColl = detailColl?.map((x: any) => {
        x.diagnosisSeverityLevel = x?.diagnosisSeverityLevel?.value;
        return x;
      });
      const detailJson = saveDetailColl?.length > 0 ? saveDetailColl : null;
      let formData = {
        diagnosisTemplateId: value?.diagnosisTemplateId,
        consumerId: userContext?.consumerId,
        organizationId: userContext?.organizationId,
        branchId: userContext?.branchId,
        title: value?.title || "",
        isNote: value?.isNote,
        note: value?.note || "",
        diagnosisTemplateDetailJson: detailJson
          ? JSON.stringify(detailJson)
          : "",
        isDeleted: value?.isDeleted,
        dataManagedBy: userContext.loginDetail.userId,
      };
      if (diagnosisTemplateId > 0) {
        result = await UpdateDiagnosisTemplate(formData);
      } else {
        result = await CreateDiagnosisTemplate(formData);
      }
      if (result?.data) {
        if (!isForPopUp) {
          navigate(-1);
        } else {
          handleClosePopUp(
            diagnosisTemplateId > 0
              ? "DIAGNOSIS_TEMPLATE_EDIT"
              : "DIAGNOSIS_TEMPLATE_CREATE",
            result?.data,
          );
        }
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: result?.message,
          alertType: AlertType.Success,
        });
      } else {
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: "Something went wrong.",
          alertType: AlertType.Error,
        });
      }
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  };

  //GET LIST OF QUICK NOTE
  const fetchQuickNotesList = async (searchValue: string, oninit: boolean) => {
    try {
      let params = new URLSearchParams();
      let quickNotesColl = [];
      params.append("ConsumerId", userContext?.consumerId?.toString());
      params.append("OrganizationId", userContext?.organizationId?.toString());
      params.append("branchId", userContext?.branchId?.toString());
      params.append("pageIndex", param.pageIndex?.toString());
      params.append("pageSize", param.pageSize?.toString());
      params.append("sortColumn", param.sortColumn);
      params.append("sortDirection", param.sortDirection);
      params.append("searchValue", searchValue);
      params.append("QuickNotesTypeCode", "DIAGNOSIS_NOTE");
      params.append("isFor", "OPD");
      params.append("Mode", "DROPDOWN");
      const result: any = await GetQuickNotesList(params);
      if (result?.data?.data?.quickNotesColl) {
        quickNotesColl = result?.data?.data?.quickNotesColl;
        if (oninit) setQuickNoteColl(quickNotesColl || []);
      }
      return quickNotesColl;
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  };

  // FILTER QUICKNOTE
  async function filterQuickNote(inputValue: string) {
    let result = await fetchQuickNotesList(inputValue, false);
    return result;
  }

  // QUICK NOTE LOAD ON SEARCH
  const loadQuickNoteList = (
    inputValue: string,
    callback: (options: any) => void,
  ) => {
    if (inputValue && inputValue.length > 0) {
      filterQuickNote(inputValue.trim()).then((r) => {
        callback(r);
      });
    } else {
      callback([]);
    }
  };

  //DIAGNOSIS FROM LIST
  const fetchDiagnosisList = async (inputValue: string, oninit: boolean) => {
    try {
      let diagnosisColl;
      let params = new URLSearchParams();
      params.append("ConsumerId", userContext?.consumerId.toString());
      params.append("OrgId", userContext?.organizationId.toString());
      params.append("BranchId", userContext?.branchId.toString());
      params.append("pageIndex", param.pageIndex?.toString());
      params.append("pageSize", param.pageSize?.toString());
      params.append("sortColumn", param.sortColumn);
      params.append("sortDirection", param.sortDirection);
      params.append("searchValue", inputValue);
      params.append("isFor", "OPD");
      params.append("mode", "DROPDOWN");

      const result: any = await GetDiagnosisList(params);

      if (result?.data?.data?.diagnosisColl) {
        diagnosisColl = result?.data?.data.diagnosisColl;
        if (oninit) setDiagnosisData(diagnosisColl);
      }
      return diagnosisColl;
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  };

  // DIAGNOSIS INSTRUCTION LOAD ON SEARCH
  const loadDiagnosisList = (
    inputValue: string,
    callback: (options: any) => void,
  ) => {
    if (inputValue && inputValue.length > 0) {
      filterDiagnosisList(inputValue?.trim()).then((r) => {
        callback(r);
      });
    } else {
      callback([]);
    }
  };

  async function filterDiagnosisList(inputValue: string) {
    let result = await fetchDiagnosisList(inputValue, false);
    return result;
  }

  // ON SUBMIT OF FORM
  const onSubmit = (data: any) => {
    saveDiagnosisTemplate(data);
  };

  // HANDLE BACK
  const handleBack = () => {
    if (isForPopUp) {
      handleClosePopUp("CLOSE");
    } else {
      navigate(-1);
    }
  };

  const filteredDiagnosisOptions = diagnosisData?.filter((x) => {
    if (
      !filtereddOPDDiagnosisDetailColl
        ?.map((o) => o?.diagnosisId?.toString())
        ?.includes(x.value)
    ) {
      return x;
    }
  });

  const handleAddDiagnosisTemplateDetail = (value: any) => {
    const detailColl = methods?.getValues().diagnosisTemplatesDetailColl || [];

    const maxRowNumber = detailColl?.reduce(
      (acc, curr) => (acc = curr.displayOrder > acc ? curr.displayOrder : acc),
      0,
    );
    const severityLevel = diagnosisOptions?.find(
      (x) => x.value === value?.diagnosisSeverityLevel,
    );
    const newRow: IDiagnosisTemplateDetail = {
      displayOrder: detailColl.length === 0 ? 0 : maxRowNumber + 1,
      diagnosisTemplateDetailId: 0,
      diagnosisTemplateId: 0,
      diagnosisId: value?.diagnosisId || 0,
      diagnosisName: value?.diagnosisName || "",
      isDeleted: false,
      mode: "I",
      diagnosisSeverityLevel: severityLevel || null,
    };

    methods.setValue("diagnosisTemplatesDetailColl", [...detailColl, newRow], {
      shouldValidate: true,
    });
  };

  // FETCH DATA
  const fetchData = async () => {
    try {
      await fetchDiagnosisList("", true);
      await fetchQuickNotesList("", true);
      if (diagnosisTemplateId > 0) {
        await getDiagnosisTemplateDetailById();
      }
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
      console.log(e);
    }
  };

  // USEEFFECTS
  useEffect(() => {
    setIsLoading(true);
    fetchData();
  }, [diagnosisTemplateId]);

  return (
    <Box>
      <Box className={"main-container"}>
        {!isForPopUp && (
          <Grid container className="page-header">
            <Grid item xs={6} sm={6} md={6}>
              <Box
                sx={{
                  display: "flex",
                  height: "100%",
                  alignItems: "center",
                }}
              >
                <CustomBreadcrumbs />
              </Box>
            </Grid>
            <Grid item xs={6} sm={6} md={6}>
              <Box className="page-header-icon">
                <Button
                  color="primary"
                  variant="outlined"
                  type="button"
                  className="secondary-button"
                  startIcon={<ArrowBackIcon />}
                  onClick={handleBack}
                >
                  Back
                </Button>
              </Box>
            </Grid>
          </Grid>
        )}

        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)} action="">
            <Grid container gap={2}>
              <Grid item xs={12} mb={3} className="group-container">
                <Grid container item spacing={2}>
                  <Grid item xs={12} sm={6} md={4} lg={3}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${
                          methods.formState.errors.title
                            ? "validation-message"
                            : ""
                        }`}
                      >
                        Template Name
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={isLoading}
                        className="skeleton-input"
                      >
                        <CustomTextField
                          placeholder="Ex. Diagnosis for ...."
                          autoCapitalize="none"
                          type="text"
                          fullWidth
                          id="title"
                          size="small"
                          autoFocus
                          {...methods.register("title")}
                          error={!!methods.formState.errors.title}
                          className={`text-input ${
                            methods.formState.errors.title ? "error" : ""
                          }`}
                        />
                      </SkeletonWrapper>
                      <Typography className="validation-message">
                        {methods.formState.errors.title?.message}
                      </Typography>
                    </FormControl>
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={8}
                    lg={9}
                    display={{ xs: "none", sm: "grid" }}
                  ></Grid>

                  <Grid item xs={12} lg={6} md={6}>
                    <Box
                      sx={{
                        border: "1px solid #0000001f",
                        borderRadius: "8px",
                        overflow: "hidden",
                        backgroundColor: "#ffffff",
                      }}
                    >
                      <Box className="inner-title-container">
                        <Box
                          className="template-box"
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            backgroundColor: "#e1eaf1",
                            height: "60px",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            <Typography>Diagnosis</Typography>
                          </Box>
                        </Box>
                        <Divider />
                        {
                          <DraggableList
                            items={filtereddOPDDiagnosisDetailColl || []}
                            setItem={handleopdDiagnosisChange}
                            Styledcard={DiagnosisDetailCard}
                            selected={selected?.displayOrder}
                          />
                        }
                        <Box
                          sx={{
                            padding: "10px",
                            paddingLeft: "20px",
                            paddingRight: "20px",
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 1,
                          }}
                        >
                          {diagnosisData?.map((item, index) => (
                            <Box
                              className="quick-chip"
                              key={index}
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={async () => {
                                if (item && !isLoading) {
                                  if (item.__isNew__ === true) {
                                    handleClickOpen(
                                      true,
                                      item.label,
                                      "DIAGNOSIS",
                                    );
                                  } else {
                                    setIsLoading(true);
                                    handleAddDiagnosisTemplateDetail(item);
                                    // await saveDiagnosisTemplate();
                                    setIsLoading(false);
                                  }
                                }
                              }}
                            >
                              {item?.diagnosisName}
                            </Box>
                          ))}

                          <FormControl fullWidth>
                            <SimpleAsyncSelect
                              placeholder="Search or Create New Diagnosis"
                              id={"OPDDiagnosisSearch"}
                              FieldName={"OPDDiagnosisSearch"}
                              key={asyncSelectKey?.OPDDiagnosisSearch?.toString()}
                              menuPortalTarget={document.querySelector("body")}
                              handleChange={async (e: any) => {
                                if (e) {
                                  if (e?.__isNew__ === true) {
                                    handleClickOpen(true, e.label, "DIAGNOSIS");
                                  } else {
                                    handleDiagnosisDetailAdd(e);
                                  }
                                }
                              }}
                              value={null}
                              loadOptions={loadDiagnosisList}
                              defaultOptions={filteredDiagnosisOptions}
                              CustomOptionComponent={diagnosisOptionComponent}
                              isSearchable
                            />
                          </FormControl>
                        </Box>
                        <Divider />
                        <Box
                          sx={{
                            padding: "10px",
                            paddingLeft: "20px",
                            paddingRight: "20px",
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 1,
                          }}
                        >
                          <Box>
                            {methods.getValues().isNote === false && (
                              <Button
                                variant="text"
                                style={{ fontSize: "6px" }}
                                onClick={() => {
                                  methods.setValue("isNote", true, {
                                    shouldValidate: true,
                                  });
                                }}
                              >
                                <AddIcon className="add-icon" />
                                <span className="add-icon"> Note</span>
                              </Button>
                            )}
                          </Box>
                          {methods.getValues().isNote === true && (
                            <FormControl fullWidth>
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  alignSelf: "end",
                                  minWidth: "100%",
                                }}
                              >
                                <FormLabel
                                  sx={{ paddingLeft: 1 }}
                                  className="fileds-label-text"
                                >
                                  Note
                                </FormLabel>
                                <Button
                                  variant="text"
                                  onClick={() => {
                                    methods.setValue("isNote", false, {
                                      shouldValidate: true,
                                    });
                                    methods.setValue("note", "", {
                                      shouldValidate: true,
                                    });
                                  }}
                                >
                                  <RemoveIcon className="remove-note" />
                                  <span className="remove-note">
                                    Remove Note
                                  </span>
                                </Button>
                              </Box>
                              <Box>
                                <Grid container spacing={2}>
                                  <Grid item xs={12}>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        flexWrap: "wrap",
                                        gap: 1,
                                        backgroundColor: "#ffffff",
                                      }}
                                    >
                                      {quickNoteColl?.map((item, index) => (
                                        <Box
                                          className="quick-chip"
                                          key={index}
                                          onMouseDown={(e) =>
                                            e.preventDefault()
                                          }
                                          onClick={() => {
                                            insertTextAtCursor(
                                              diagnosisQuillRef,
                                              item?.quickNotesName,
                                            );
                                          }}
                                        >
                                          {parse(item?.quickNotesName)}
                                        </Box>
                                      ))}
                                      <Grid xs={12}>
                                        <Box
                                          style={{
                                            paddingBottom: "8px",
                                            width: "100%",
                                          }}
                                        >
                                          <FormControl fullWidth>
                                            <SimpleAsyncSelect
                                              placeholder="Search or create Quick Note"
                                              id={"QuickNoteSearch"}
                                              key={asyncSelectKey?.QuickNoteSearch?.toString()}
                                              menuPortalTarget={document.body}
                                              handleChange={(e: any) => {
                                                if (e) {
                                                  insertTextAtCursor(
                                                    diagnosisQuillRef,
                                                    e?.label,
                                                  );
                                                }
                                              }}
                                              value={null}
                                              onCreateOption={(
                                                inputValue: any,
                                              ) => {
                                                handleClickOpenCreatable(
                                                  true,
                                                  inputValue,
                                                  "QUICKNOTE",
                                                );
                                              }}
                                              loadOptions={loadQuickNoteList}
                                              defaultOptions={quickNoteColl}
                                              isSearchable
                                              CustomOptionComponent={
                                                quicknotesOptionComponent
                                              }
                                              FieldName={""}
                                            />
                                          </FormControl>
                                        </Box>
                                      </Grid>
                                    </Box>
                                  </Grid>
                                </Grid>

                                <SkeletonWrapper
                                  isLoading={isLoading}
                                  className="skeleton-box"
                                >
                                  <Controller
                                    control={methods.control}
                                    name={"note"}
                                    render={({ field }) => (
                                      <ReactQuill
                                        {...field}
                                        ref={diagnosisQuillRef}
                                        theme="snow"
                                        value={methods.getValues("note") ?? ""}
                                        className={`react-quill-alignment`}
                                      />
                                    )}
                                  />
                                </SkeletonWrapper>
                              </Box>
                            </FormControl>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    {!mdd && (
                      <Box
                        sx={{
                          border: "1px solid #0000001f",
                          borderRadius: "8px",
                          minHeight: 200,
                          overflow: "hidden",
                          backgroundColor: "#ffffff",
                        }}
                        style={{
                          position: "sticky",
                          top: "5px",
                        }}
                      >
                        <Box className="inner-title-container">
                          <Box
                            className="template-box"
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              backgroundColor: "#e1eaf1",
                              height: "60px",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                              }}
                            >
                              <Typography>Diagnosis Summary</Typography>
                            </Box>
                          </Box>
                          <Divider />
                          {methods?.getValues()?.note?.trim().length > 0 ||
                          filtereddOPDDiagnosisDetailColl?.length > 0 ? (
                            <Box
                              sx={{
                                padding: "16px 20px",
                                fontWeight: 500,
                                fontSize: "13px",
                              }}
                            >
                              <Box>
                                {filtereddOPDDiagnosisDetailColl?.length >
                                  0 && (
                                  <Box sx={{ paddingLeft: "17px" }}>
                                    {filtereddOPDDiagnosisDetailColl.map(
                                      (item, index) => (
                                        <li
                                          style={{ marginBottom: "8px" }}
                                          key={index}
                                        >
                                          <strong style={{ fontSize: "14px" }}>
                                            {item?.diagnosisName}
                                          </strong>
                                        </li>
                                      ),
                                    )}
                                  </Box>
                                )}
                                {methods?.getValues()?.note?.trim().length >
                                  0 && (
                                  <Box sx={{ paddingLeft: "17px" }}>
                                    {parse(methods.getValues()?.note)}
                                  </Box>
                                )}
                              </Box>
                            </Box>
                          ) : (
                            <Box
                              sx={{
                                display: "flex",
                                gap: 2,
                                justifyContent: "center",
                                alignItems: "center",
                                height: "150px",
                              }}
                            >
                              <CloudQueueOutlinedIcon
                                color="info"
                                sx={{ height: "30px", width: "30px" }}
                              />
                              <div>No summary found</div>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    )}
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: "flex", justifyContent: "end", gap: 2 }}>
                  <LoadingButton
                    color="primary"
                    loadingPosition="start"
                    startIcon={<></>}
                    variant="contained"
                    type="submit"
                    className="primary-button"
                    name="save"
                    disabled={isLoading || methods.formState.isSubmitting}
                  >
                    {diagnosisTemplateId > 0 ? "Update" : "Save"}
                  </LoadingButton>
                  <LoadingButton
                    color="primary"
                    loadingPosition="start"
                    startIcon={<></>}
                    variant="outlined"
                    type="button"
                    className="secondary-button"
                    name="cancel"
                    disabled={isLoading || methods.formState.isSubmitting}
                    onClick={handleBack}
                  >
                    Cancel
                  </LoadingButton>
                </Box>
              </Grid>
            </Grid>
          </form>
        </FormProvider>

        {/* DIAGNOSIS POPUP  */}
        <Dialog
          className="dialogPage"
          open={customPopUp.open && customPopUp.code === "DIAGNOSIS"}
          onClose={(e, reason) => {
            if (reason !== "backdropClick") {
              handleClose();
            }
          }}
          fullScreen
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          PaperProps={{
            style: {
              width: "600px",
              height: "300px",
              maxWidth: "none",
            },
          }}
        >
          <AppBar sx={{ position: "relative" }}>
            <Toolbar>
              <Typography
                style={{ fontWeight: 600, marginLeft: 0 }}
                sx={{ ml: 2, flex: 1 }}
                variant="h6"
              >
                Save Diagnosis
              </Typography>
            </Toolbar>
            <Grid item xs={12}>
              <Divider />
            </Grid>
          </AppBar>
          <DialogContent sx={{ padding: "12px 24px" }}>
            <DiagnosisMasterSave
              handleClosePopUp={(code: string, data: any) => {
                handleManagePopup(code, data);
              }}
              isForPopUp={customPopUp.open}
              name={customPopUp.name}
            />
          </DialogContent>
          <Divider />
        </Dialog>

        <Dialog
          className="dialogPage"
          open={customPopUp.open && customPopUp.code === "QUICKNOTE"}
          onClose={(e, reason) => {
            if (reason !== "backdropClick") {
              handleClose();
            }
          }}
          fullScreen={smd}
        >
          <AppBar sx={{ position: "relative" }}>
            <Toolbar>
              <Typography
                style={{ fontWeight: 600, marginLeft: 0 }}
                sx={{ ml: 2, flex: 1 }}
                variant="h6"
              >
                Save Quick Note
              </Typography>
            </Toolbar>
            <Grid item xs={12}>
              <Divider />
            </Grid>
          </AppBar>
          <DialogContent sx={{ padding: "12px 24px" }}>
            <QuickNotesSave
              handleClosePopUp={(code: string, company: any) => {
                handleManagePopup(code, company);
              }}
              isForPopUp={customPopUp.open}
              name={customPopUp.name}
              quickNoteType="diagnosis"
            />
          </DialogContent>

          <Divider />
        </Dialog>
        {/* DIAGNOSIS severity level DIALOG */}
        <Dialog
          className="dialogPage"
          open={diagnosisSeverityLevelDialog.open}
          onClose={() =>
            setDiagnosisSeverityLevelDialog({ open: false, value: null })
          }
          fullScreen={smd}
          PaperProps={{
            style: { width: smd ? "100%" : "700px", maxWidth: "none" },
          }}
        >
          <AppBar sx={{ position: "relative" }}>
            <Toolbar>
              <Typography sx={{ flex: 1, fontWeight: 600 }} variant="h6">
                Diagnosis Severity Level
              </Typography>
              <Button
                variant="text"
                onClick={() =>
                  setDiagnosisSeverityLevelDialog({
                    open: false,
                    value: null,
                  })
                }
              >
                <span className="add-icon"> Save</span>
              </Button>
            </Toolbar>
          </AppBar>
          <DialogContent
            sx={{
              padding: "0 !important",
            }}
          >
            {renderDiagnosisSeverityLevelDialogContent()}
          </DialogContent>
        </Dialog>
      </Box>
    </Box>
  );
}
